// Navigation centralisée de l'application.
//
// REGISTRE CENTRAL des sections (SECTIONS_MENU) : pour ajouter une page au menu
// plus tard, il suffit d'ajouter une entrée ici (et d'ajouter son id à SectionId +
// son rendu dans App). Le menu se construit automatiquement à partir de cette liste,
// sur toutes les pages — rien d'autre à toucher.

import { createContext, useContext, type ReactNode } from 'react'

// Sections accessibles directement depuis le menu (destinations « racine »).
// Les sous-écrans contextuels (création/détail/impression de fiche) n'en font pas
// partie : on s'y rend depuis une section, pas depuis le menu.
export type SectionId = 'accueil' | 'calculateur' | 'formulateur' | 'ingredients' | 'formules' | 'articles' | 'stock' | 'clients' | 'devis' | 'factures' | 'depots' | 'comptabilite' | 'fiches' | 'fiches-cosmetique' | 'reglages' | 'comptes'

export interface EntreeMenu {
  id: SectionId
  emoji: string
  label: string
  // Visible uniquement pour le compte admin (Thérèse). Absent = visible par tout le monde.
  adminOnly?: boolean
  // Visible uniquement si l'utilisatrice possède ce module (ex. 'cosmetique'). Absent = toujours visible.
  moduleRequis?: string
}

// Ordre d'affichage du menu (déconnexion gérée à part, en pied de menu).
export const SECTIONS_MENU: readonly EntreeMenu[] = [
  { id: 'accueil', emoji: '📊', label: 'Tableau de bord' },
  { id: 'calculateur', emoji: '🧼', label: 'Calculateur de saponification' },
  { id: 'formulateur', emoji: '⚗️', label: 'Formulateur cosmétique', moduleRequis: 'cosmetique' },
  { id: 'ingredients', emoji: '🧪', label: 'Mes ingrédients' },
  { id: 'formules', emoji: '📒', label: 'Mes formules' },
  { id: 'fiches', emoji: '📋', label: 'Lots de production' },
  { id: 'fiches-cosmetique', emoji: '⚗️', label: 'Lots cosmétiques', moduleRequis: 'cosmetique' },
  { id: 'articles', emoji: '🏷️', label: 'Mes articles' },
  { id: 'stock', emoji: '📦', label: 'Stock' },
  { id: 'clients', emoji: '👤', label: 'Mes clients' },
  { id: 'devis', emoji: '🧾', label: 'Mes devis' },
  { id: 'factures', emoji: '📑', label: 'Mes factures' },
  { id: 'depots', emoji: '🏪', label: 'Dépôts-vente' },
  { id: 'comptabilite', emoji: '💶', label: 'Recettes & achats' },
  { id: 'reglages', emoji: '⚙️', label: 'Réglages' },
  { id: 'comptes', emoji: '🗂️', label: 'Comptes clientes', adminOnly: true },
]

interface NavValue {
  // Section à mettre en évidence dans le menu (déduite de l'écran courant).
  sectionCourante: SectionId
  // Va vers une section (App gère les détails, ex. calculateur vierge). Peut être
  // bloquée par un garde de navigation (cf. definirGardeNavigation).
  naviguer: (section: SectionId) => void
  // Enregistre un garde consulté avant chaque navigation : il renvoie false pour
  // ANNULER le départ (ex. modifications non enregistrées). null = aucun garde.
  definirGardeNavigation: (garde: (() => boolean) | null) => void
}

const NavContext = createContext<NavValue | null>(null)

export function NavProvider({ value, children }: { value: NavValue; children: ReactNode }) {
  return <NavContext.Provider value={value}>{children}</NavContext.Provider>
}

export function useNav(): NavValue {
  const ctx = useContext(NavContext)
  if (!ctx) throw new Error('useNav doit être utilisé dans un NavProvider.')
  return ctx
}
